import { Box, Group, Menu, Stack, Text, Title, useMantineColorScheme } from "@mantine/core";
import { IconPointFilled } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "react-i18next";

import classes from "./NotificationItem.module.css";

import { TextSerif } from "@/components/elements";
import { IconMoreHorizontal } from "@/components/icons";

import type { Notification } from "@/types/notification";
import type { ElementType, FC, JSX } from "react";

dayjs.extend(relativeTime);

export type NotificationItemProps = {
  component?: ElementType<any, keyof JSX.IntrinsicElements>;
  onClick?: (id: string) => void;
  onRead?: (id: string) => void;
} & Notification;

export const NotificationItem: FC<NotificationItemProps> = ({
  component,
  date,
  id,
  message,
  onClick,
  status,
  onRead,
}) => {
  const { t } = useTranslation();

  const unread = status === "delivered";

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const colorDate = isDark ? "#828282" : "#666666";
  const colorText = isDark ? "#a7a7a7" : "#191919";
  const colorDot = isDark ? "#a1b0e0" : "#143ab1";
  const colorThreeDots = isDark ? "#a1b0e0" : "#666666";

  let notificationTitle = "";
  let notificationMessage = message;

  try {
    const parsedMessage = JSON.parse(message);

    notificationTitle = parsedMessage.title ?? "";
    notificationMessage = parsedMessage.message ?? message;
  } catch {
    notificationMessage = message;
  }

  return (
    <Box
      component={component as any}
      onClick={() => onClick?.(id)}
      px="lg"
      py="md"
      className={`${classes.root} ${unread ? classes.unread : ""}`}
      style={{ overflow: "hidden", maxWidth: "100%" }}
    >
      <Stack gap="md">
        <Group
          c={unread ? "primary" : undefined}
          justify="space-between"
          align="center"
          wrap="nowrap"
        >
          <Title
            order={6}
            fw="bold"
            tt="uppercase"
            title={notificationTitle}
            lineClamp={1}
            className={classes.title}
            style={{ flex: 1, minWidth: 0 }}
          >
            {notificationTitle}
          </Title>

          <Menu position="bottom-end" shadow="md" withinPortal={false}>
            <Menu.Target>
              <Box
                onClick={(event) => event.stopPropagation()}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <IconMoreHorizontal size={18} color={colorThreeDots} />
              </Box>
            </Menu.Target>

            <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
              <Menu.Item onClick={() => onRead?.(id)}>
                {unread
                  ? t("components.NotificationMenu.markAsRead")
                  : t("components.NotificationMenu.markAsUnread")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Group justify="space-between" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          <TextSerif
            fz="xs"
            c={colorText}
            fw={400}
            lh="xs"
            style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
          >
            {notificationMessage}
          </TextSerif>

          {unread && (
            <Box style={{ flexShrink: 0 }}>
              <IconPointFilled size={12} color={colorDot} />
            </Box>
          )}
        </Group>

        <Text c={colorDate} fz="0.7em" title={date}>
          {dayjs(date).fromNow()}
        </Text>
      </Stack>
    </Box>
  );
};
